import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Globe } from 'lucide-react';
import SourceCombobox from '../common/SourceCombobox';
import {
  RegionalOption,
  fetchRegionalOptions,
} from '../../constants/countryRegionalOptions';

export interface CurrencySettingsData {
  baseCurrency: string;
  displayCurrency: string;
  timezone: string;
  dateFormat: string;
}

interface CurrencySettingsProps {
  data: CurrencySettingsData;
  onChange: (data: CurrencySettingsData) => void;
}

const getOptionLabel = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string
) =>
  options.find(
    option => option.value.toLowerCase() === String(value).toLowerCase()
  )?.label || '';

const CurrencySettings = ({ data, onChange }: CurrencySettingsProps) => {
  const [currencyOptions, setCurrencyOptions] = useState<RegionalOption[]>([]);
  const [timezoneOptions, setTimezoneOptions] = useState<RegionalOption[]>([]);
  const [isRegionalOptionsLoading, setIsRegionalOptionsLoading] =
    useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadRegionalOptions = async () => {
      try {
        setIsRegionalOptionsLoading(true);
        const { currencyOptions, timezoneOptions } = await fetchRegionalOptions(
          {
            currency: true,
            timezone: true,
            currencyValueFormat: 'lower',
          }
        );

        if (!isMounted) return;

        setCurrencyOptions(currencyOptions);
        setTimezoneOptions(timezoneOptions);
      } catch (error) {
        if (!isMounted) return;
        setCurrencyOptions([]);
        setTimezoneOptions([]);
      } finally {
        if (isMounted) {
          setIsRegionalOptionsLoading(false);
        }
      }
    };

    loadRegionalOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (field: keyof CurrencySettingsData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-500/10 rounded-lg">
            <Globe className="h-5 w-5 text-orange-500" />
          </div>
          <div>
            <CardTitle>Currency Settings</CardTitle>
            <CardDescription>
              Configure currency and regional preferences
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="base-currency">Base Currency</Label>
          <SourceCombobox
            id="base-currency"
            items={currencyOptions.map(option => option.label)}
            placeholder="Select base currency"
            value={getOptionLabel(currencyOptions, data.baseCurrency)}
            onChange={value => {
              const option = currencyOptions.find(item => item.label === value);
              if (option) handleChange('baseCurrency', option.value);
            }}
            disabled={isRegionalOptionsLoading}
            emptyText="No currencies found."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="display-currency">Display Currency</Label>
          <SourceCombobox
            id="display-currency"
            items={currencyOptions.map(option => option.label)}
            placeholder="Select display currency"
            value={getOptionLabel(currencyOptions, data.displayCurrency)}
            onChange={value => {
              const option = currencyOptions.find(item => item.label === value);
              if (option) handleChange('displayCurrency', option.value);
            }}
            disabled={isRegionalOptionsLoading}
            emptyText="No currencies found."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <SourceCombobox
            id="timezone"
            items={timezoneOptions.map(option => option.label)}
            placeholder="Select timezone"
            value={getOptionLabel(timezoneOptions, data.timezone)}
            onChange={value => {
              const option = timezoneOptions.find(item => item.label === value);
              if (option) handleChange('timezone', option.value);
            }}
            disabled={isRegionalOptionsLoading}
            emptyText="No timezones found."
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrencySettings;
